import { z } from "zod";
import type { ALLERGENS, TAGS } from "./schemas/catalog";
import type { KITCHEN_STATION_TYPES } from "./schemas/orders";

/**
 * «Готовые меню» — платные наборы по формату заведения (шаурмичная, кофейня, кафе-мороженое…): продукты с ценами закупки,
 * полуфабрикаты с ТТК, категории, позиции с описанием, фото, вариантами, модификаторами и ТТК. Покупаются один раз
 * (счёт платформы), загружаются в меню компании сколько угодно раз (повтор не дублирует).
 * Цены в данных — в сомах (KGS, целые): позиции — за порцию, продукты — за кг | л | шт; при загрузке переводятся в minor
 * и в валюту филиала по `PACK_FX`.
 */
export type PackUnit = "g" | "ml" | "pcs";
export type PackProduct = {
  name: string;
  /** ingredient по умолчанию; goods — товар для перепродажи (бутылка), packaging — упаковка */
  type?: "ingredient" | "goods" | "packaging" | "consumable";
  unit: PackUnit;
  /** Цена закупки, сом за кг (g) | л (ml) | шт (pcs) */
  price: number;
  /** Категория продуктов (группа на складе) */
  category: string;
  allergens?: (typeof ALLERGENS)[number][];
  shelfLifeDays?: number;
  /** г/мл — для перевода единиц */
  density?: number;
};
/** Строка ТТК: ключ продукта или полуфабриката, брутто в базовой единице (г | мл | шт), отход и потери при обработке, % */
export type PackLine = [product: string, qty: number, opts?: { waste?: number; loss?: number; takeaway?: boolean }];
export type PackSemi = {
  name: string;
  unit: PackUnit;
  /** Выход в базовой единице (1000 г соуса) */
  output: number;
  items: PackLine[];
  instructions: string;
  shelfLifeDays?: number;
  cookTime?: number;
  category?: string;
};
export type PackModifier = { name: string; price: number; product?: string; qty?: number };
export type PackModifierGroup = { name: string; single?: boolean; min?: number; max?: number; options: PackModifier[] };
/** Вариант (размер): factor — множитель граммовок ТТК основной позиции (шт не умножаются) */
export type PackVariant = { name: string; price: number; factor?: number };
export type PackItem = {
  /** Ключ в наборе (латиница) — имя фото `<key>.webp` */
  key: string;
  name: string;
  description: string;
  price: number;
  type?: "dish" | "drink" | "product";
  station?: (typeof KITCHEN_STATION_TYPES)[number];
  weight?: number;
  volume?: number;
  calories?: number;
  allergens?: (typeof ALLERGENS)[number][];
  tags?: (typeof TAGS)[number][];
  cookTime?: number;
  variants?: PackVariant[];
  /** Ключи групп модификаторов набора */
  mods?: string[];
  /** Товар без ТТК — списывается сам (ключ продукта типа goods) */
  goods?: string;
  recipe?: PackLine[];
  instructions?: string;
  /** Запрос для поиска фото (англ.) */
  photo: string;
};
export type PackCategory = { name: string; icon: string; items: PackItem[] };
export type MenuPack = {
  code: string;
  name: string;
  /** Одна строка для карточки витрины */
  tagline: string;
  description: string;
  /** Формат заведения (онбординг) — для подсказки «подходит вам» */
  formats: string[];
  products: Record<string, PackProduct>;
  semis?: Record<string, PackSemi>;
  groups?: Record<string, PackModifierGroup>;
  categories: PackCategory[];
};

/** Курсы для перевода цен набора из сомов (1 сом = N единиц валюты), с округлением до «красивых» цен при загрузке */
export const PACK_FX: Record<string, number> = { KGS: 1, KZT: 5.8, UZS: 145, RUB: 1, TJS: 0.125, USD: 0.0115, EUR: 0.0105, TRY: 0.39, AZN: 0.0195, GEL: 0.031, AMD: 4.5, BYN: 0.037, MDL: 0.2, TMT: 0.04 };

/** Цена продукта набора за базовую единицу, сом (за 1 г | 1 мл | 1 шт) */
export const packUnitPrice = (p: PackProduct) => (p.unit === "pcs" ? p.price : p.price / 1000);

export type PackCost = {
  /** Себестоимость порции (зал), сом */
  cost: number;
  /** С упаковкой на вынос, сом */
  takeawayCost: number;
};

/** Цена базовой единицы полуфабриката, сом; рекурсивно, с защитой от циклов */
function semiUnitPrice(pack: MenuPack, key: string, stack: string[]): number {
  const s = pack.semis?.[key];
  if (!s) throw new Error(`${pack.code}: нет полуфабриката ${key}`);
  if (stack.includes(key)) throw new Error(`${pack.code}: цикл ПФ ${[...stack, key].join(" → ")}`);
  return linesCost(pack, s.items, [...stack, key]).cost / s.output;
}

/** Себестоимость строк ТТК, сом (брутто × цена; упаковка «на вынос» — отдельно) */
export function linesCost(pack: MenuPack, lines: PackLine[], stack: string[] = []): PackCost {
  let cost = 0;
  let takeaway = 0;
  for (const [key, qty, o] of lines) {
    const product = pack.products[key];
    const unit = product ? packUnitPrice(product) : semiUnitPrice(pack, key, stack);
    const v = qty * unit;
    if (o?.takeaway) takeaway += v;
    else cost += v;
  }
  return { cost, takeawayCost: cost + takeaway };
}

/** Себестоимость позиции: ТТК или товар; для варианта — с множителем (шт не умножаются) */
export function packItemCost(pack: MenuPack, item: PackItem, factor = 1): PackCost {
  if (item.goods) {
    const p = pack.products[item.goods];
    if (!p) throw new Error(`${pack.code}: нет товара ${item.goods}`);
    return { cost: packUnitPrice(p), takeawayCost: packUnitPrice(p) };
  }
  return linesCost(pack, scaleLines(pack, item.recipe ?? [], factor));
}

/** Граммовки варианта: всё, кроме штучного, × factor */
export function scaleLines(pack: MenuPack, lines: PackLine[], factor: number): PackLine[] {
  if (factor === 1) return lines;
  return lines.map(([k, q, o]) => {
    const unit = pack.products[k]?.unit ?? pack.semis?.[k]?.unit;
    return [k, unit === "pcs" ? q : Math.round(q * factor * 10) / 10, o];
  });
}

/** Перевод цены набора (сом) в minor валюты с округлением до «красивой» суммы (позиции меню) */
export function packMenuPrice(som: number, currency: string) {
  const rate = PACK_FX[currency] ?? 1;
  const v = som * rate;
  const step = v >= 10_000 ? 500 : v >= 1000 ? 50 : v >= 100 ? 5 : v >= 10 ? 1 : 0.1;
  return Math.round((Math.round(v / step) * step) * 100);
}

/** Цена закупки продукта за базовую единицу, minor (дробная — до 6 знаков) */
export function packPurchasePrice(p: PackProduct, currency: string) {
  return Math.round(packUnitPrice(p) * (PACK_FX[currency] ?? 1) * 100 * 1e6) / 1e6;
}

/** Все позиции набора */
export const packItems = (pack: MenuPack) => pack.categories.flatMap((c) => c.items);

/** Проверка целостности набора: ссылки на продукты/ПФ/группы, единственность ключей, разумный food cost. */
export function validatePack(pack: MenuPack): string[] {
  const errors: string[] = [];
  const has = (k: string) => !!pack.products[k] || !!pack.semis?.[k];
  const keys = new Set<string>();
  for (const k of Object.keys(pack.semis ?? {})) if (pack.products[k]) errors.push(`ключ ${k} — и продукт, и ПФ`);
  for (const [k, s] of Object.entries(pack.semis ?? {})) {
    for (const [p] of s.items) if (!has(p)) errors.push(`ПФ ${k}: нет продукта ${p}`);
    try {
      semiUnitPrice(pack, k, []);
    } catch (e) {
      errors.push((e as Error).message);
    }
  }
  for (const it of packItems(pack)) {
    if (keys.has(it.key)) errors.push(`повтор ключа ${it.key}`);
    keys.add(it.key);
    if (!/^[a-z0-9_]+$/.test(it.key)) errors.push(`${it.key}: ключ — латиница, цифры, _`);
    if (!it.goods && !it.recipe?.length) errors.push(`${it.key}: нет ТТК`);
    if (it.goods && pack.products[it.goods]?.type !== "goods") errors.push(`${it.key}: товар ${it.goods} должен быть типа goods`);
    for (const [p] of it.recipe ?? []) if (!has(p)) errors.push(`${it.key}: нет продукта ${p}`);
    for (const g of it.mods ?? []) if (!pack.groups?.[g]) errors.push(`${it.key}: нет группы ${g}`);
    if (it.description.length < 40) errors.push(`${it.key}: короткое описание`);
    try {
      const pct = (packItemCost(pack, it).cost / it.price) * 100;
      const max = it.goods ? 85 : 50;
      if (!(pct > 3 && pct < max)) errors.push(`${it.key}: food cost ${pct.toFixed(1)}%`);
    } catch (e) {
      errors.push((e as Error).message);
    }
  }
  for (const [g, grp] of Object.entries(pack.groups ?? {})) for (const o of grp.options) if (o.product && (!has(o.product) || !o.qty)) errors.push(`группа ${g}: ${o.name} — продукт/кол-во`);
  return errors;
}

// ───────── API ─────────

export const menuPackImportSchema = z.strictObject({
  /** Ключи позиций; пусто — весь набор */
  items: z.array(z.string().regex(/^[a-z0-9_]+$/)).max(500).default([]),
  /** Фото позиций и ТТК (с продуктами и полуфабрикатами) */
  photos: z.boolean().default(true),
  recipes: z.boolean().default(true),
  /** Позиции создаются скрытыми с кассы и сайта — включить после проверки */
  inactive: z.boolean().default(false),
});
export type MenuPackImportInput = z.input<typeof menuPackImportSchema>;

/** Цена набора в Platform Admin: минорные единицы по валютам, видимость */
export const menuPackSettingsSchema = z.strictObject({
  prices: z.record(z.string().regex(/^[A-Z]{3}$/), z.number().int().min(0).max(1e10)),
  isPublic: z.boolean(),
});
export const MENU_PACK_DEFAULT_PRICE_KGS = 6500;

/**
 * Несколько наборов сразу (Platform Admin): цена в сомах — set (задать, minor), percent (±%, округление до сома), amount (±minor);
 * наборы «не продаётся» (без цены KGS) меняет только set. Результат не меньше 0.
 */
export const menuPackBulkSchema = z
  .strictObject({
    codes: z.array(z.string().regex(/^[a-z0-9_-]{1,40}$/)).min(1, { error: "Выберите хотя бы один набор" }).max(200),
    price: z
      .strictObject({ mode: z.enum(["set", "percent", "amount"]), value: z.number().int().min(-1e10).max(1e10) })
      .refine((v) => v.mode !== "set" || v.value >= 0, { error: "Цена не может быть отрицательной", path: ["value"] })
      .refine((v) => v.mode === "set" || v.value !== 0, { error: "Изменение не может быть нулевым", path: ["value"] })
      .refine((v) => v.mode !== "percent" || (v.value >= -90 && v.value <= 500), { error: "Процент — от −90 до 500", path: ["value"] })
      .optional(),
    isPublic: z.boolean().optional(),
  })
  .refine((v) => v.price !== undefined || v.isPublic !== undefined, { error: "Выберите, что изменить", path: ["codes"] });

/** Новая цена KGS набора; undefined — набор остаётся «не продаётся». */
export function bulkPackPrice(current: number | undefined, price: { mode: "set" | "percent" | "amount"; value: number }): number | undefined {
  if (price.mode === "set") return price.value;
  if (current === undefined) return undefined;
  const next = price.mode === "percent" ? Math.round((current * (100 + price.value)) / 100 / 100) * 100 : current + price.value;
  return Math.max(0, next);
}
